import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { BaseRouteParams } from 'hocs'
import { useGetMembershipDetailsQuery } from '../reduxQueries'
import classNames from 'classnames'
import GeneralSection from './sections/general/general'
import MemberSection from './sections/member/member'
import BillingSection from './sections/billing/billing'
import PaymentsSection from './sections/payments/payments'
import { CancelMembershipModal } from './modals/cancelMembershipModal/cancelMembershipModal'
import UpdateMemberModal from './modals/updateMemberModal/updateMemberModal'
import RefundPaymentModal from './modals/refundPaymentModal/refundPaymentModal'
import { Member, MembershipPayment } from 'memberships/schema'
import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons'
import { IconProp } from '@fortawesome/fontawesome'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMembershipMutations } from './hooks/useMembershipMutations'
import { showErrorMessage } from 'uiComponents/message'
import './membershipDetailsPanel.scss'

const getPossessive = (name: string) => (name.endsWith('s') ? `${name}'` : `${name}'s`)

interface MembershipDetailsPanelProps {
    membershipUuid: string
    onClose: () => void
}

const MembershipDetailsPanel: React.FC<MembershipDetailsPanelProps> = ({ membershipUuid, onClose }) => {
    const { accountSlug } = useParams<BaseRouteParams>()
    const [isClosing, setIsClosing] = useState(false)
    const [showCancelMembershipModal, setShowCancelMembershipModal] = useState(false)
    const [showUpdateModal, setShowUpdateModal] = useState(false)
    const [paymentToRefund, setPaymentToRefund] = useState<MembershipPayment | null>(null)

    const {
        isUpdatingMember,
        isRefundingPayment,
        isUpdatingMembershipStatus,
        handleUpdateMember,
        handleRefundPayment,
        handleCancelMembership,
        handleDisableAutoRenewal,
    } = useMembershipMutations(accountSlug, membershipUuid)

    const {
        data: membership,
        isLoading,
        isFetching,
    } = useGetMembershipDetailsQuery(
        { accountSlug, uuid: membershipUuid || '' },
        { skip: !membershipUuid || !accountSlug },
    )

    if (!membershipUuid || !accountSlug) return null

    const handleClose = () => {
        setIsClosing(true)
        setTimeout(() => {
            setIsClosing(false)
            onClose()
        }, 200)
    }

    const onCancelMembership = () => {
        setShowCancelMembershipModal(false)
        handleCancelMembership().catch(() => {
            showErrorMessage('Membership could not be cancelled.', 'cancel_membership_error')
        })
    }

    const onDisableAutoRenewal = () => {
        setShowCancelMembershipModal(false)
        handleDisableAutoRenewal().catch(() => {
            showErrorMessage('Auto-renewal could not be disabled.', 'disable_auto_renewal_error')
        })
    }

    const onUpdateMember = (values: Member, photo: string | null) => {
        setShowUpdateModal(false)
        handleUpdateMember(values, photo).catch(() => {
            showErrorMessage('Membership could not be updated.', 'update_membership_error')
        })
    }

    const onRefundPayment = (payment: MembershipPayment) => {
        setPaymentToRefund(null)
        handleRefundPayment(payment).catch(() => {
            showErrorMessage('Payment could not be refunded.', 'refund_membership_payment_error')
        })
    }

    const memberNamePossessive = getPossessive(membership?.member?.firstName || '')
    const pageTitle = membership?.member?.firstName
        ? `${memberNamePossessive} ${membership?.article.name}`
        : membership?.article.name

    return (
        <>
            <div
                className={classNames('membership-details-panel-overlay', { closing: isClosing })}
                onClick={handleClose}
            />
            <div className={classNames('membership-details-panel', { closing: isClosing, loading: isLoading })}>
                <div className="membership-details-header">
                    <h4 className="membership-details-title">{pageTitle}</h4>
                    <button className="close-button" onClick={handleClose}>
                        &times;
                    </button>
                </div>
                {isLoading || membership ? (
                    <div className="membership-details-content">
                        <GeneralSection
                            membership={membership}
                            openCancelMembershipModal={() => setShowCancelMembershipModal(true)}
                            isLoading={isLoading}
                            isLoadingMembershipStatus={isLoading || isUpdatingMembershipStatus || isFetching}
                        />
                        <MemberSection
                            member={membership?.member}
                            openUpdateModal={() => setShowUpdateModal(true)}
                            isLoading={isLoading || isUpdatingMember}
                            accountSlug={accountSlug}
                        />
                        <BillingSection billing={membership?.billing} isLoading={isLoading} />
                        <PaymentsSection
                            payments={membership?.payments || []}
                            accountSlug={accountSlug}
                            setPaymentToRefund={setPaymentToRefund}
                            isLoading={isLoading || isRefundingPayment || isFetching}
                        />
                    </div>
                ) : (
                    <div className="membership-details-error">
                        <FontAwesomeIcon icon={faExclamationTriangle as IconProp} />
                        <div>Membership details could not be loaded</div>
                    </div>
                )}
                {membership && (
                    <>
                        {showCancelMembershipModal && (
                            <CancelMembershipModal
                                membership={membership}
                                onClose={() => setShowCancelMembershipModal(false)}
                                onCancelMembership={onCancelMembership}
                                onDisableAutoRenewal={onDisableAutoRenewal}
                            />
                        )}
                        {showUpdateModal && (
                            <UpdateMemberModal
                                membership={membership}
                                onClose={() => setShowUpdateModal(false)}
                                onUpdate={onUpdateMember}
                            />
                        )}
                        {paymentToRefund && (
                            <RefundPaymentModal
                                payment={paymentToRefund}
                                onClose={() => setPaymentToRefund(null)}
                                onRefund={onRefundPayment}
                            />
                        )}
                    </>
                )}
            </div>
        </>
    )
}

export default MembershipDetailsPanel
